/**
 * AI CONTEXT
 * Purpose: the grouped moment stream for the Standout Review dashboard — one
 *   Collapse panel per group from `groupMoments`, all open by default, each
 *   headed by its counts (`countGroupStates`). Student mode (the default)
 *   is one card per kid per class session: the name once, then notes,
 *   Story Time, and class scans as divider rows in that card, with a
 *   select-all checkbox and one "Share N" button. A shared row keeps that
 *   checkbox, turned green, and unchecking it unshares the row. Rows are
 *   images, then Story Time, then teacher comments, then TA comments by
 *   name. Groups and the moments inside them are newest first.
 * Used by: StandoutReviewView (Stream mode).
 */
import { UploadOutlined } from "@ant-design/icons"
import { App, Button, Checkbox, Collapse, Empty, Space, Typography } from "antd"
import dayjs from "dayjs"
import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import Masonry from "react-masonry-css"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Firebase from "#classes/Firebase"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import {
  buildArtworkSharedUpdate,
  type ReviewClassImage,
} from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import { type StandoutReviewGroupBy } from "#customTypes/globalTypes"
import { invariant } from "#customTypes/types"
import { standoutAuthorName } from "#forms/AddStudent/studentProfileTabHelpers"
import { knownEventEndsBySessionId } from "#utils/familyDelivery"
import {
  cancelFamilyDelivery,
  scheduleDeliveriesForMoments,
} from "#utils/familyDeliveryWrite"
import { quickStoryScoreMatches } from "#utils/quickStoryScore"

import ClassImageReviewList, {
  ClassImageReviewRow,
  ClassImageStudentSections,
} from "./ClassImageReviewList"
import {
  type FamilyDeliveryApi,
  FamilyDeliveryProvider,
  useFamilyDelivery,
} from "./familyDeliveryContext"
import QuickStoryScoreMark from "./QuickStoryScoreMark"
import SendCountdownLabel from "./SendCountdownLabel"
import {
  FEED_CARD_BORDER,
  FEED_DIVIDER,
  FEED_HEADER_BG,
  FEED_RAIL,
  FEED_REVIEW_PILL,
  FEED_ROLE_PILL,
  FEED_UNHEARD_PILL,
} from "./StandoutFeedRow"
import StandoutMomentCard from "./StandoutMomentCard"
import { classScanAuthor, feedStaffRole } from "./standoutReviewCardHelpers"
import {
  countFamilyReviewBuckets,
  countGroupStates,
  describeMomentLevel,
  familyReviewAutoSummary,
  familyReviewBucket,
  groupMoments,
  type StandoutReviewGroup,
  type StandoutReviewJoins,
  type StandoutReviewSubgroup,
} from "./standoutReviewHelpers"
import {
  buildStudentSessionGroups,
  compareSessionFeedEntries,
  itemsSharedMessage,
  levelForStudentSession,
  otherFamilyItemsShared,
  sessionShareKey,
  sessionSubtitleForNesting,
  type StudentSessionGroup,
  unsharedTargets,
} from "./standoutSessionGroups"
import StoryTimeReviewList, {
  StoryTimeReviewRow,
  StoryTimeStudentSections,
} from "./StoryTimeReviewList"
import { useKeyedSingleFlight } from "./useSingleFlight"

const { Text } = Typography

/** One session card. Tiles columns are about this wide. */
const SESSION_CARD_MAX_PX = 700

const SESSION_MASONRY_CSS = `
.sr-session-masonry { display: flex; width: auto; margin-left: -12px; }
.sr-session-masonry-col { padding-left: 12px; background-clip: padding-box; }
.sr-session-masonry-col > div { margin-bottom: 12px; }
`

const SessionCardStack = ({
  mosaic,
  children,
}: {
  mosaic: boolean
  children: ReactNode
}) => {
  if (!mosaic) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "flex-start",
        }}
      >
        {children}
      </div>
    )
  }
  return (
    <Masonry
      breakpointCols={{
        default: 4,
        2200: 3,
        1500: 2,
        760: 1,
      }}
      className="sr-session-masonry"
      columnClassName="sr-session-masonry-col"
    >
      {children}
    </Masonry>
  )
}

interface StandoutReviewStreamProps {
  moments: readonly StandoutMomentConstructor[]
  storyTimes?: readonly StoryTimeConstructor[]
  classImages?: readonly ReviewClassImage[]
  groupBy: StandoutReviewGroupBy
  joins: StandoutReviewJoins
  emptyText: string
  onApproveForFamily: (
    moments: readonly StandoutMomentConstructor[]
  ) => Promise<boolean>
  /** UPDATE grant — gates the "· N needs work" fragment in group/subgroup headers. */
  canSeeNeedsWork: boolean
  /** Same UPDATE grant — family approve is a moderation action. */
  canApproveFamily: boolean
  /** studentId__classSessionId → notifyAt for deliveries still scheduled. */
  notifyAtByKey?: Record<string, string>
}

/** Shared by the group and subgroup headers — same counts, same needs-work gate. */
const CountsText = ({
  moments,
  canSeeNeedsWork,
}: {
  moments: readonly StandoutMomentConstructor[]
  canSeeNeedsWork: boolean
}) => {
  const counts = countGroupStates(moments)
  return (
    <Text type="secondary">
      {counts.total} {counts.total === 1 ? "moment" : "moments"}
      {canSeeNeedsWork && counts.needsWork > 0
        ? ` · ${counts.needsWork} needs work`
        : ""}
    </Text>
  )
}

const GroupHeader = ({
  group,
  canSeeNeedsWork,
}: {
  group: StandoutReviewGroup
  canSeeNeedsWork: boolean
}) => (
  <Space size={8} wrap>
    <Text strong data-testid="sr-group-label">
      {group.label}
    </Text>
    <Text type="secondary">—</Text>
    <CountsText moments={group.moments} canSeeNeedsWork={canSeeNeedsWork} />
  </Space>
)

/** Teacher sub-header inside a class group — no mark-viewed action here, the
 * group-level button already covers all its subgroups' moments. */
const SubgroupHeader = ({
  subgroup,
  canSeeNeedsWork,
}: {
  subgroup: StandoutReviewSubgroup
  canSeeNeedsWork: boolean
}) => (
  <Space size={8} wrap>
    <Text type="secondary" strong style={{ fontSize: 12 }}>
      {subgroup.label}
    </Text>
    <CountsText moments={subgroup.moments} canSeeNeedsWork={canSeeNeedsWork} />
  </Space>
)

const GroupBody = ({
  group,
  joins,
  canSeeNeedsWork,
  storyTimes = [],
  classImages = [],
}: {
  group: StandoutReviewGroup
  joins: StandoutReviewJoins
  canSeeNeedsWork: boolean
  storyTimes?: readonly StoryTimeConstructor[]
  classImages?: readonly ReviewClassImage[]
}) => (
  <div
    data-testid="sr-group"
    data-group-key={group.key}
    style={{ display: "flex", flexDirection: "column", gap: 8 }}
  >
    {group.subgroups
      ? group.subgroups.map((subgroup) => (
          <div
            key={subgroup.key}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <div data-testid="sr-subgroup" data-teacher-id={subgroup.key}>
              <SubgroupHeader
                subgroup={subgroup}
                canSeeNeedsWork={canSeeNeedsWork}
              />
            </div>
            {subgroup.moments.map((moment) => (
              <StandoutMomentCard
                key={moment.id}
                moment={moment}
                joins={joins}
                hideTeacher
              />
            ))}
          </div>
        ))
      : group.moments.map((moment) => (
          <StandoutMomentCard key={moment.id} moment={moment} joins={joins} />
        ))}
    <StoryTimeReviewList storyTimes={storyTimes} />
    <ClassImageReviewList classImages={classImages} />
  </div>
)

type SessionFeedEntry =
  | {
      kind: "moment"
      at: number
      id: string
      moment: StandoutMomentConstructor
    }
  | { kind: "story"; at: number; id: string; storyTime: StoryTimeConstructor }
  | { kind: "image"; at: number; id: string; row: ReviewClassImage }

const SessionFeed = ({
  group,
  moments,
  joins,
  canApproveFamily,
  selectedIds,
  eligibleIds,
  onToggleTarget,
  onAudioHeard,
  storySelection,
  imageSelection,
  showGroupTestId = true,
  leading,
}: {
  group: StudentSessionGroup
  moments: readonly StandoutMomentConstructor[]
  joins: StandoutReviewJoins
  canApproveFamily: boolean
  selectedIds: ReadonlySet<string>
  eligibleIds: ReadonlySet<string>
  onToggleTarget: (shareKey: string, checked: boolean) => void
  onAudioHeard: (heardKey: string) => void
  storySelection: (
    storyTime: StoryTimeConstructor
  ) => { checked: boolean; onChange: (checked: boolean) => void } | undefined
  imageSelection: (
    row: ReviewClassImage
  ) => { checked: boolean; onChange: (checked: boolean) => void } | undefined
  /** Class panels already carry sr-group. Nested student cards must not. */
  showGroupTestId?: boolean
  /** Select-all sits in the rail so it lines up with the row checkboxes. */
  leading?: ReactNode
}) => {
  const entries = useMemo(() => {
    const next: SessionFeedEntry[] = [
      ...moments.map((moment) => ({
        kind: "moment" as const,
        at: dayjs(moment.createdAt).valueOf(),
        id: moment.id,
        moment,
      })),
      ...group.storyTimes.map((storyTime) => ({
        kind: "story" as const,
        at: storyTime.timeCreated ?? 0,
        id: storyTime.id,
        storyTime,
      })),
      ...group.classImages.map((row) => ({
        kind: "image" as const,
        at: row.artwork.timeCreated ?? 0,
        id: row.artwork.id,
        row,
      })),
    ]
    return [...next].sort((a, b) =>
      compareSessionFeedEntries(
        {
          kind: a.kind,
          at: a.at,
          id: a.id,
          taName:
            a.kind === "moment" && a.moment.assistantId?.trim()
              ? standoutAuthorName(
                  a.moment,
                  joins.usersById,
                  joins.studentsById
                )
              : undefined,
        },
        {
          kind: b.kind,
          at: b.at,
          id: b.id,
          taName:
            b.kind === "moment" && b.moment.assistantId?.trim()
              ? standoutAuthorName(
                  b.moment,
                  joins.usersById,
                  joins.studentsById
                )
              : undefined,
        }
      )
    )
  }, [group.classImages, group.storyTimes, joins, moments])

  if (entries.length === 0 && !leading) return null

  return (
    <div
      data-testid={showGroupTestId ? "sr-group" : undefined}
      data-group-key={group.key}
      style={{ borderLeft: `3px solid ${FEED_RAIL}` }}
    >
      {leading}
      {entries.map((entry) => {
        if (entry.kind === "moment") {
          const key = sessionShareKey({ kind: "moment", id: entry.moment.id })
          return (
            <StandoutMomentCard
              key={key}
              moment={entry.moment}
              joins={joins}
              variant="feed"
              onAudioHeard={onAudioHeard}
              selection={
                canApproveFamily && eligibleIds.has(key)
                  ? {
                      checked: selectedIds.has(key),
                      onChange: (checked) => onToggleTarget(key, checked),
                    }
                  : undefined
              }
            />
          )
        }
        if (entry.kind === "story") {
          const user = entry.storyTime.createdBy
            ? joins.usersById[entry.storyTime.createdBy]
            : undefined
          return (
            <StoryTimeReviewRow
              key={sessionShareKey({
                kind: "storyTime",
                id: entry.storyTime.id,
              })}
              storyTime={entry.storyTime}
              variant="feed"
              authorName={user?.name?.trim() || "Teacher"}
              roleLabel={feedStaffRole(false, user?.role)}
              selection={storySelection(entry.storyTime)}
              onAudioHeard={onAudioHeard}
            />
          )
        }
        const scanAuthor = classScanAuthor(
          entry.row.artwork,
          joins.usersById,
          joins.studentsById
        )
        return (
          <ClassImageReviewRow
            key={sessionShareKey({
              kind: "image",
              studentId: entry.row.studentId,
              artworkId: entry.row.artwork.id,
            })}
            row={entry.row}
            variant="feed"
            authorName={scanAuthor?.name}
            roleLabel={scanAuthor?.role}
            selection={imageSelection(entry.row)}
          />
        )
      })}
    </div>
  )
}

const settledLine = (
  label: string,
  counts: { sent: number; undeliverable: number },
  autoSummary: string | null
): string => {
  const detail = [
    autoSummary,
    counts.sent > 0 ? `${counts.sent} sent` : null,
    counts.undeliverable > 0 ? `${counts.undeliverable} no session` : null,
  ].filter((part): part is string => part !== null)
  return detail.length === 0
    ? `${label} — nothing pending`
    : `${label} — nothing pending · ${detail.join(" · ")}`
}

const StudentGroupBlock = ({
  group,
  joins,
  canApproveFamily,
  selectedIds,
  foldOpen,
  onToggleFold,
  onToggleTarget,
  onToggleStudent,
  onApprove,
  approving = false,
  showGroupTestId = true,
  hideClassName = false,
}: {
  group: StudentSessionGroup
  joins: StandoutReviewJoins
  canApproveFamily: boolean
  selectedIds: ReadonlySet<string>
  foldOpen: boolean
  onToggleFold: (groupKey: string) => void
  onToggleTarget: (shareKey: string, checked: boolean) => void
  onToggleStudent: (group: StudentSessionGroup, checked: boolean) => void
  onApprove: (group: StudentSessionGroup) => void
  /** Send to Parents is writing; the button spins and the selection holds. */
  approving?: boolean
  showGroupTestId?: boolean
  /** Class panels already name the class. The nested student card shows the date. */
  hideClassName?: boolean
}) => {
  const bucketCounts = countFamilyReviewBuckets(group.moments)
  const eligibleIds = useMemo(
    () => unsharedTargets(group).map(sessionShareKey),
    [group]
  )
  const eligibleIdSet = useMemo(() => new Set(eligibleIds), [eligibleIds])
  const selectedCount = eligibleIds.filter((id) => selectedIds.has(id)).length
  const allSelected =
    eligibleIds.length > 0 && selectedCount === eligibleIds.length

  const handleToggleStudent = useCallback(
    (checked: boolean) => {
      onToggleStudent(group, checked)
    },
    [group, onToggleStudent]
  )

  const handleApprove = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onApprove(group)
    },
    [group, onApprove]
  )

  const handleShowSent = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onToggleFold(group.key)
    },
    [group.key, onToggleFold]
  )

  const visibleMoments = useMemo(
    () =>
      group.moments.filter(
        (moment) => foldOpen || familyReviewBucket(moment) !== "sent"
      ),
    [foldOpen, group.moments]
  )

  const storySelection = useCallback(
    (storyTime: StoryTimeConstructor) => {
      const key = sessionShareKey({ kind: "storyTime", id: storyTime.id })
      if (!canApproveFamily || !eligibleIdSet.has(key)) return undefined
      return {
        checked: selectedIds.has(key),
        onChange: (checked: boolean) => onToggleTarget(key, checked),
      }
    },
    [canApproveFamily, eligibleIdSet, onToggleTarget, selectedIds]
  )
  const imageSelection = useCallback(
    (row: ReviewClassImage) => {
      const key = sessionShareKey({
        kind: "image",
        studentId: row.studentId,
        artworkId: row.artwork.id,
      })
      if (!canApproveFamily || !eligibleIdSet.has(key)) return undefined
      return {
        checked: selectedIds.has(key),
        onChange: (checked: boolean) => onToggleTarget(key, checked),
      }
    },
    [canApproveFamily, eligibleIdSet, onToggleTarget, selectedIds]
  )

  const [heardKeys, setHeardKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const handleAudioHeard = useCallback((heardKey: string) => {
    setHeardKeys((previous) => {
      if (previous.has(heardKey)) return previous
      const next = new Set(previous)
      next.add(heardKey)
      return next
    })
  }, [])
  const unheardCount = useMemo(() => {
    const keys: string[] = []
    for (const moment of group.moments) {
      for (const entry of moment.media ?? []) {
        if (entry.kind === "audio") keys.push(`audio:${entry.id}`)
      }
    }
    for (const storyTime of group.storyTimes) keys.push(`story:${storyTime.id}`)
    return keys.filter((key) => !heardKeys.has(key)).length
  }, [group.moments, group.storyTimes, heardKeys])

  const headerScores = useMemo(
    () =>
      group.moments.flatMap((moment) => {
        const note = moment.note?.trim() ?? ""
        if (!note || moment.category === "secret") return []
        if (!quickStoryScoreMatches(moment.quickStoryScore, note)) return []
        return [{ id: moment.id, note, score: moment.quickStoryScore }]
      }),
    [group.moments]
  )

  const delivery = useFamilyDelivery()
  const notifyAt = delivery.notifyAt(group.studentId, group.classSessionId)
  const student = joins.studentsById[group.studentId]
  const autoSummary = familyReviewAutoSummary(group.moments)
  const reviewParts = [
    `${eligibleIds.length} to review`,
    `${bucketCounts.sent} sent`,
    autoSummary,
  ].filter((part): part is string => part !== null)
  if (bucketCounts.undeliverable > 0) {
    reviewParts.push(`${bucketCounts.undeliverable} no session`)
  }

  const cardStyle = {
    border: `1px solid ${FEED_CARD_BORDER}`,
    borderRadius: 16,
    background: "#fff",
    overflow: "hidden" as const,
    maxWidth: SESSION_CARD_MAX_PX,
    width: "100%",
  }

  const settled =
    bucketCounts.pending === 0 &&
    bucketCounts.undeliverable === 0 &&
    eligibleIds.length === 0
  const momentsInFeed = settled
    ? foldOpen
      ? group.moments
      : []
    : visibleMoments
  const showNotes =
    group.moments.length > 0 && (settled || bucketCounts.sent > 0)
  const sessionLevel = levelForStudentSession(group, joins)
  const levelLabel = sessionLevel ? describeMomentLevel(sessionLevel) : null
  const selectAll =
    canApproveFamily && !settled && eligibleIds.length > 0 ? (
      <AnimateInFlex
        animation={ANIMATION_BY_KEY.down}
        gap={12}
        style={{
          alignItems: "center",
          padding: "14px 16px",
          borderTop: `1px solid ${FEED_DIVIDER}`,
          width: "100%",
        }}
      >
        <span data-testid="sr-student-select" data-student-id={group.studentId}>
          <Checkbox
            aria-label={`Select items to share for ${group.label}`}
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            disabled={approving}
            onChange={(event) => handleToggleStudent(event.target.checked)}
          />
        </span>
        <Button
          type="link"
          size="small"
          data-testid="sr-select-all"
          data-student-id={group.studentId}
          style={{ padding: 0, height: "auto" }}
          disabled={approving}
          onClick={() => handleToggleStudent(!allSelected)}
        >
          select all {eligibleIds.length}
        </Button>
      </AnimateInFlex>
    ) : null

  return (
    <div style={cardStyle}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "14px 16px 12px",
          background: FEED_HEADER_BG,
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div data-testid="sr-group-label">
              {student ? (
                <ClickableStudentInfoRow student={student} />
              ) : (
                <Text strong style={{ fontSize: 16 }}>
                  {group.label}
                </Text>
              )}
            </div>
            {levelLabel ? (
              <span data-testid="sr-student-level" style={FEED_ROLE_PILL}>
                {levelLabel}
              </span>
            ) : null}
            {headerScores.map((entry) => (
              <QuickStoryScoreMark
                key={entry.id}
                phase="ready"
                score={entry.score}
                note={entry.note}
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            {notifyAt ? <SendCountdownLabel notifyAt={notifyAt} /> : null}
            {unheardCount > 0 ? (
              <span style={FEED_UNHEARD_PILL}>♪ {unheardCount} not heard</span>
            ) : null}
            {settled ? null : (
              <span
                data-testid="sr-family-counts"
                data-student-id={group.studentId}
                style={FEED_REVIEW_PILL}
              >
                {reviewParts.join(" · ")}
              </span>
            )}
            {canApproveFamily && !settled ? (
              <Button
                data-testid="sr-approve-family"
                data-student-id={group.studentId}
                disabled={selectedCount === 0}
                loading={approving}
                icon={<UploadOutlined />}
                onClick={handleApprove}
                style={{ borderRadius: 10, fontWeight: 600 }}
              >
                Send {selectedCount} to Parents
              </Button>
            ) : null}
            {showNotes ? (
              <Button
                data-testid="sr-show-folded"
                data-student-id={group.studentId}
                data-fold={settled ? "settled" : "sent"}
                size="small"
                onClick={handleShowSent}
              >
                {foldOpen ? "Hide" : "Show"}
              </Button>
            ) : null}
          </div>
        </div>
        <div>
          <Text type="secondary" data-testid="sr-session-label">
            {sessionSubtitleForNesting(group.subtitle, hideClassName)}
          </Text>
          {settled ? (
            <div
              data-testid="sr-student-settled"
              data-student-id={group.studentId}
            >
              <Text type="success">
                {settledLine(group.label, bucketCounts, autoSummary)}
              </Text>
            </div>
          ) : null}
        </div>
      </div>
      <SessionFeed
        group={group}
        moments={momentsInFeed}
        joins={joins}
        canApproveFamily={canApproveFamily && !settled}
        selectedIds={selectedIds}
        eligibleIds={eligibleIdSet}
        onToggleTarget={onToggleTarget}
        onAudioHeard={handleAudioHeard}
        storySelection={storySelection}
        imageSelection={imageSelection}
        showGroupTestId={showGroupTestId}
        leading={selectAll}
      />
    </div>
  )
}

/**
 * Inside a class panel: one card per kid per class session, so that kid's
 * notes, Story Time, and scan stay together instead of a teacher list with
 * the recording and the photo parked at the bottom.
 */
const ClassStudentCards = ({
  groups,
  joins,
  canApproveFamily,
  selectedIds,
  shownFoldIds,
  onToggleFold,
  onToggleTarget,
  onToggleStudent,
  onApprove,
  approvingKeys,
  mosaic,
}: {
  groups: readonly StudentSessionGroup[]
  joins: StandoutReviewJoins
  canApproveFamily: boolean
  selectedIds: ReadonlySet<string>
  shownFoldIds: ReadonlySet<string>
  onToggleFold: (groupKey: string) => void
  onToggleTarget: (shareKey: string, checked: boolean) => void
  onToggleStudent: (group: StudentSessionGroup, checked: boolean) => void
  onApprove: (group: StudentSessionGroup) => void
  approvingKeys: ReadonlySet<string>
  mosaic: boolean
}) => {
  return (
    <SessionCardStack mosaic={mosaic}>
      {groups.map((group) => (
        <StudentGroupBlock
          key={group.key}
          group={group}
          joins={joins}
          canApproveFamily={canApproveFamily}
          selectedIds={selectedIds}
          foldOpen={shownFoldIds.has(group.key)}
          onToggleFold={onToggleFold}
          onToggleTarget={onToggleTarget}
          onToggleStudent={onToggleStudent}
          onApprove={onApprove}
          approving={approvingKeys.has(group.key)}
          showGroupTestId={false}
          hideClassName
        />
      ))}
    </SessionCardStack>
  )
}

const StandoutReviewStream = ({
  moments,
  storyTimes = [],
  classImages = [],
  groupBy,
  joins,
  emptyText,
  onApproveForFamily,
  canSeeNeedsWork,
  canApproveFamily,
  notifyAtByKey = {},
}: StandoutReviewStreamProps) => {
  const { message } = App.useApp()
  const { currentUserId } = useUserContext()
  const [mosaic, setMosaic] = useState(false)
  const deliveryApi = useMemo<FamilyDeliveryApi>(
    () => ({
      reset: (studentId, classSessionId) => {
        if (!classSessionId) return
        void scheduleDeliveriesForMoments(
          [{ studentId, classSessionId }],
          knownEventEndsBySessionId(joins.sessionsByClassId)
        ).catch((err) => {
          console.error("[standout] schedule family delivery failed", err)
          void message.error(
            "Saved the change, but the send timer did not start. Try again."
          )
        })
      },
      unshared: (studentId, classSessionId, excluding) => {
        if (!classSessionId) return
        if (
          otherFamilyItemsShared(
            { moments, storyTimes, classImages },
            studentId,
            classSessionId,
            excluding
          )
        ) {
          return
        }
        void cancelFamilyDelivery(studentId, classSessionId).catch((err) => {
          console.error("[standout] cancel family delivery failed", err)
        })
      },
      notifyAt: (studentId, classSessionId) => {
        if (!classSessionId) return null
        return notifyAtByKey[`${studentId}__${classSessionId}`] ?? null
      },
    }),
    [
      classImages,
      joins.sessionsByClassId,
      message,
      moments,
      notifyAtByKey,
      storyTimes,
    ]
  )
  const showMosaicToggle = groupBy === "student" || groupBy === "class"
  const mosaicToggle = showMosaicToggle ? (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        data-testid="sr-mosaic-toggle"
        size="small"
        type={mosaic ? "primary" : "default"}
        onClick={() => setMosaic((open) => !open)}
      >
        {mosaic ? "One column" : "Tiles"}
      </Button>
    </div>
  ) : null
  const groups = useMemo(
    () => groupMoments(moments, groupBy, joins),
    [groupBy, joins, moments]
  )
  // Same session cards in student view and inside a class panel. The allow-list
  // for row checkboxes and Share has to be these groups. An empty list makes
  // a click no-op, and the cleanup below would then drop the header selection.
  const classSessionGroupsByKey = useMemo(() => {
    const byKey = new Map<string, StudentSessionGroup[]>()
    if (groupBy !== "class") return byKey
    for (const group of groups) {
      byKey.set(
        group.key,
        buildStudentSessionGroups(
          group.moments,
          storyTimes.filter((storyTime) => storyTime.classId === group.key),
          classImages.filter((row) => row.artwork.classId === group.key),
          joins
        )
      )
    }
    return byKey
  }, [classImages, groupBy, groups, joins, storyTimes])
  const sessionGroups = useMemo(() => {
    if (groupBy === "student") {
      return buildStudentSessionGroups(moments, storyTimes, classImages, joins)
    }
    if (groupBy === "class") {
      return [...classSessionGroupsByKey.values()].flat()
    }
    return []
  }, [
    classImages,
    classSessionGroupsByKey,
    groupBy,
    joins,
    moments,
    storyTimes,
  ])

  // Expansion is controlled as "everything open except what the user closed":
  // `defaultActiveKey` is only read on mount, so a group that appears after a
  // filter or group-by change would otherwise render collapsed. Closed keys
  // are remembered PER GROUPING MODE — the shared "no record" bucket key
  // would otherwise carry a collapse from Class mode into Teacher mode
  // (Bugbot, PR #109).
  const [closedKeysByMode, setClosedKeysByMode] = useState<
    Partial<Record<StandoutReviewGroupBy, string[]>>
  >({})
  const closedKeys = useMemo(
    () => closedKeysByMode[groupBy] ?? [],
    [closedKeysByMode, groupBy]
  )
  const setClosedKeys = useCallback(
    (next: string[]) => {
      setClosedKeysByMode((previous) => ({ ...previous, [groupBy]: next }))
    },
    [groupBy]
  )
  const activeKeys = useMemo(
    () =>
      groups
        .map((group) => group.key)
        .filter((key) => !closedKeys.includes(key)),
    [closedKeys, groups]
  )
  const handleActiveChange = useCallback(
    (openKeys: string | string[]) => {
      const open = Array.isArray(openKeys) ? openKeys : [openKeys]
      setClosedKeys(
        groups.map((group) => group.key).filter((key) => !open.includes(key))
      )
    },
    [groups, setClosedKeys]
  )

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [shownFoldIds, setShownFoldIds] = useState<ReadonlySet<string>>(
    () => new Set()
  )

  useEffect(() => {
    setShownFoldIds(new Set())
  }, [groupBy])

  // Drop a selection that is no longer an unshared item on a session card.
  // Student view and class view share this list. Other groupings have no
  // session cards, so an empty list clears whatever was checked.
  useEffect(() => {
    setSelectedIds((previous) => {
      if (previous.size === 0) return previous
      const allowed = new Set<string>()
      for (const group of sessionGroups) {
        for (const target of unsharedTargets(group)) {
          allowed.add(sessionShareKey(target))
        }
      }
      let changed = false
      const next = new Set<string>()
      for (const id of previous) {
        if (allowed.has(id)) next.add(id)
        else changed = true
      }
      return changed ? next : previous
    })
  }, [sessionGroups])

  const { pendingKeys: approvingKeys, run: runApprove } = useKeyedSingleFlight()

  const handleToggleFold = useCallback((studentId: string) => {
    setShownFoldIds((previous) => {
      const next = new Set(previous)
      if (next.has(studentId)) next.delete(studentId)
      else next.add(studentId)
      return next
    })
  }, [])

  const handleToggleTarget = useCallback(
    (shareKey: string, checked: boolean) => {
      setSelectedIds((previous) => {
        const next = new Set(previous)
        if (!checked) {
          next.delete(shareKey)
          return next
        }
        const allowed = new Set(
          sessionGroups.flatMap((group) =>
            unsharedTargets(group).map(sessionShareKey)
          )
        )
        if (!allowed.has(shareKey)) return previous
        next.add(shareKey)
        return next
      })
    },
    [sessionGroups]
  )

  const handleToggleStudent = useCallback(
    (group: StudentSessionGroup, checked: boolean) => {
      const eligible = unsharedTargets(group).map(sessionShareKey)
      const eligibleSet = new Set(eligible)
      setSelectedIds((previous) => {
        const next = new Set(previous)
        for (const id of previous) {
          if (eligibleSet.has(id)) next.delete(id)
        }
        if (checked) {
          for (const id of eligible) next.add(id)
        }
        return next
      })
    },
    []
  )

  const handleApprove = useCallback(
    (group: StudentSessionGroup) => {
      const picked = unsharedTargets(group).filter((target) =>
        selectedIds.has(sessionShareKey(target))
      )
      const momentIds = new Set(
        picked.flatMap((target) =>
          target.kind === "moment" ? [target.id] : []
        )
      )
      const storyIds = new Set(
        picked.flatMap((target) =>
          target.kind === "storyTime" ? [target.id] : []
        )
      )
      const imageIds = new Set(
        picked.flatMap((target) =>
          target.kind === "image"
            ? [`${target.studentId}:${target.artworkId}`]
            : []
        )
      )
      const momentsToShare = group.moments.filter((moment) =>
        momentIds.has(moment.id)
      )
      const storiesToShare = group.storyTimes.filter((storyTime) =>
        storyIds.has(storyTime.id)
      )
      const imagesToShare = group.classImages.filter((row) =>
        imageIds.has(`${row.studentId}:${row.artwork.id}`)
      )
      invariant(currentUserId != null, "Current user id is not defined!")

      void runApprove(group.key, async () => {
        let shared = 0
        if (momentsToShare.length > 0) {
          const approved = await onApproveForFamily(momentsToShare)
          if (approved) shared += momentsToShare.length
        }
        let mediaFailed = false
        if (storiesToShare.length > 0 || imagesToShare.length > 0) {
          try {
            await Promise.all([
              ...storiesToShare.map((storyTime) =>
                Firebase.getInstance().updateDocumentOrThrow(
                  DOC_TYPES.STORY_TIMES,
                  storyTime.id,
                  { isShared: true }
                )
              ),
              ...imagesToShare.map((row) => {
                const update = buildArtworkSharedUpdate(
                  { [row.artwork.id]: row.artwork },
                  row.artwork.id,
                  true,
                  currentUserId
                )
                if (!update) {
                  return Promise.reject(
                    new Error("This scan cannot be shared.")
                  )
                }
                return Firebase.getInstance().updateDocumentOrThrow(
                  DOC_TYPES.STUDENTS,
                  row.studentId,
                  update
                )
              }),
            ])
            shared += storiesToShare.length + imagesToShare.length
          } catch (err) {
            mediaFailed = true
            console.error("[standout] batch share failed", err)
            void message.error(
              shared > 0
                ? "Notes were shared, but the Story Times and scans were not. Try those again."
                : "Could not share these items. Try again."
            )
          }
        }
        if (shared === 0 || mediaFailed) return
        void message.success(itemsSharedMessage(shared, "shared"))
        if (!group.classSessionId) return
        try {
          await scheduleDeliveriesForMoments(
            [
              {
                studentId: group.studentId,
                classSessionId: group.classSessionId,
              },
            ],
            knownEventEndsBySessionId(joins.sessionsByClassId)
          )
        } catch (scheduleErr) {
          console.error(
            "[standout] schedule family delivery failed",
            scheduleErr
          )
          void message.error(
            "Saved the share, but the send timer did not start. Try again."
          )
        }
      })
    },
    [
      currentUserId,
      joins.sessionsByClassId,
      message,
      onApproveForFamily,
      runApprove,
      selectedIds,
    ]
  )

  const frame = (body: ReactNode) => (
    <FamilyDeliveryProvider value={deliveryApi}>
      <style>{SESSION_MASONRY_CSS}</style>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {mosaicToggle}
        {body}
      </div>
    </FamilyDeliveryProvider>
  )

  if (groupBy === "student") {
    if (sessionGroups.length === 0) {
      return frame(<Empty description={emptyText} style={{ marginTop: 40 }} />)
    }
    return frame(
      <div data-testid="sr-student-stream">
        <SessionCardStack mosaic={mosaic}>
          {sessionGroups.map((group) => (
            <StudentGroupBlock
              key={group.key}
              group={group}
              joins={joins}
              canApproveFamily={canApproveFamily}
              selectedIds={selectedIds}
              foldOpen={shownFoldIds.has(group.key)}
              onToggleFold={handleToggleFold}
              onToggleTarget={handleToggleTarget}
              onToggleStudent={handleToggleStudent}
              onApprove={handleApprove}
              approving={approvingKeys.has(group.key)}
            />
          ))}
        </SessionCardStack>
      </div>
    )
  }

  if (
    groups.length === 0 &&
    storyTimes.length === 0 &&
    classImages.length === 0
  ) {
    return frame(<Empty description={emptyText} style={{ marginTop: 40 }} />)
  }

  if (groups.length === 0) {
    return frame(
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <StoryTimeStudentSections
          storyTimes={storyTimes}
          studentsById={joins.studentsById}
        />
        <ClassImageStudentSections
          classImages={classImages}
          studentsById={joins.studentsById}
        />
      </div>
    )
  }

  return frame(
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Collapse
        activeKey={activeKeys}
        onChange={handleActiveChange}
        items={groups.map((group) => ({
          key: group.key,
          label: (
            <GroupHeader group={group} canSeeNeedsWork={canSeeNeedsWork} />
          ),
          children:
            groupBy === "class" ? (
              <div
                data-testid="sr-group"
                data-group-key={group.key}
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <ClassStudentCards
                  groups={classSessionGroupsByKey.get(group.key) ?? []}
                  joins={joins}
                  canApproveFamily={canApproveFamily}
                  selectedIds={selectedIds}
                  shownFoldIds={shownFoldIds}
                  onToggleFold={handleToggleFold}
                  onToggleTarget={handleToggleTarget}
                  onToggleStudent={handleToggleStudent}
                  onApprove={handleApprove}
                  approvingKeys={approvingKeys}
                  mosaic={mosaic}
                />
              </div>
            ) : (
              <GroupBody
                group={group}
                joins={joins}
                canSeeNeedsWork={canSeeNeedsWork}
              />
            ),
        }))}
      />
      <StoryTimeStudentSections
        storyTimes={
          groupBy === "class"
            ? storyTimes.filter(
                (storyTime) =>
                  !storyTime.classId ||
                  !groups.some((group) => group.key === storyTime.classId)
              )
            : storyTimes
        }
        studentsById={joins.studentsById}
      />
      <ClassImageStudentSections
        classImages={
          groupBy === "class"
            ? classImages.filter(
                (row) =>
                  !row.artwork.classId ||
                  !groups.some((group) => group.key === row.artwork.classId)
              )
            : classImages
        }
        studentsById={joins.studentsById}
      />
    </div>
  )
}

export default StandoutReviewStream
